/** Icon set traced from the mockup's SVG paths — 16px grid, 1.3 stroke. */

type IconProps = { size?: number; color?: string; strokeWidth?: number };

const stroke = (props: IconProps) => ({
  stroke: props.color ?? "currentColor",
  strokeWidth: props.strokeWidth ?? 1.3,
  fill: "none" as const,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
});

function Svg({ size = 14, children }: { size?: number; children: React.ReactNode }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden focusable="false">
      {children}
    </svg>
  );
}

export const Icon = {
  file: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <rect x="2" y="2.5" width="12" height="11" rx="1.5" {...stroke(p)} />
      <path d="M2 6h12" {...stroke(p)} />
    </Svg>
  ),
  save: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M8 2v8" {...stroke(p)} />
      <path d="M4.5 7 L8 10.5 L11.5 7" {...stroke(p)} />
      <path d="M2.5 13.5h11" {...stroke(p)} />
    </Svg>
  ),
  search: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <circle cx="7" cy="7" r="4.6" {...stroke(p)} />
      <path d="M10.4 10.4 L14 14" {...stroke(p)} />
    </Svg>
  ),
  logs: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <rect x="2" y="2" width="3" height="12" fill={p.color ?? "currentColor"} />
      <rect x="6.8" y="2" width="2.2" height="12" fill={p.color ?? "currentColor"} opacity=".55" />
      <rect x="10.8" y="2" width="2.2" height="12" fill={p.color ?? "currentColor"} opacity=".55" />
    </Svg>
  ),
  curve: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M2 13 L5.5 4.5 L9 11 L14 2.5" {...stroke(p)} />
    </Svg>
  ),
  crossplot: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M2 14V2" {...stroke(p)} />
      <path d="M2 14h12" {...stroke(p)} />
      <circle cx="5.5" cy="10.5" r="1.6" fill={p.color ?? "currentColor"} />
      <circle cx="9" cy="6.5" r="1.6" fill={p.color ?? "currentColor"} />
      <circle cx="12.4" cy="9" r="1.6" fill={p.color ?? "currentColor"} />
    </Svg>
  ),
  correlation: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <rect x="2" y="2" width="4.5" height="12" {...stroke(p)} />
      <rect x="9.5" y="2" width="4.5" height="12" {...stroke(p)} />
      <path d="M6.5 6.5 h3" {...stroke(p)} strokeDasharray="2 1.5" />
    </Svg>
  ),
  map: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M2 4 L6 2.5 L10 4.5 L14 3 V12 L10 13.5 L6 11.5 L2 13 Z" {...stroke(p)} />
      <path d="M6 2.5v9M10 4.5v9" {...stroke(p)} />
    </Svg>
  ),
  table: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <rect x="2" y="2.5" width="12" height="11" {...stroke(p)} />
      <path d="M2 6h12M8 6v7.5" {...stroke(p)} />
    </Svg>
  ),
  analysis: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M8 2 L13.5 5 v6 L8 14 L2.5 11 V5 z" {...stroke(p)} />
      <circle cx="8" cy="8" r="2" {...stroke(p)} />
    </Svg>
  ),
  print: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M4.5 6V2.5h7V6" {...stroke(p)} />
      <rect x="2" y="6" width="12" height="5" rx="1" {...stroke(p)} />
      <path d="M4.5 9.5h7v4h-7z" {...stroke(p)} />
    </Svg>
  ),
  upload: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M8 12V3.5" {...stroke(p)} />
      <path d="M4.8 6.5 L8 3.2 L11.2 6.5" {...stroke(p)} />
      <path d="M2.5 13.5h11" {...stroke(p)} />
    </Svg>
  ),
  plus: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M8 3v10M3 8h10" {...stroke(p)} />
    </Svg>
  ),
  minus: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M3 8h10" {...stroke(p)} />
    </Svg>
  ),
  dots: (p: IconProps = {}) => (
    <svg width={p.size ?? 4} height={(p.size ?? 4) * 3} viewBox="0 0 4 12" aria-hidden>
      <circle cx="2" cy="2" r="1.1" fill={p.color ?? "currentColor"} />
      <circle cx="2" cy="6" r="1.1" fill={p.color ?? "currentColor"} />
      <circle cx="2" cy="10" r="1.1" fill={p.color ?? "currentColor"} />
    </svg>
  ),
  grip: (p: IconProps = {}) => (
    <svg width={p.size ?? 8} height={(p.size ?? 8) * 1.25} viewBox="0 0 8 10" aria-hidden>
      <path d="M1 2h6M1 5h6M1 8h6" stroke={p.color ?? "currentColor"} strokeWidth="1.1" />
    </svg>
  ),
  eye: (p: IconProps = {}) => (
    <svg width={p.size ?? 12} height={((p.size ?? 12) * 3) / 4} viewBox="0 0 12 9" aria-hidden>
      <path d="M1 4.5 C3 1.5 9 1.5 11 4.5 C9 7.5 3 7.5 1 4.5 Z" {...stroke(p)} strokeWidth={1.1} />
      <circle cx="6" cy="4.5" r="1.3" fill={p.color ?? "currentColor"} />
    </svg>
  ),
  eyeOff: (p: IconProps = {}) => (
    <svg width={p.size ?? 12} height={((p.size ?? 12) * 3) / 4} viewBox="0 0 12 9" aria-hidden>
      <path d="M1 4.5 C3 1.5 9 1.5 11 4.5 C9 7.5 3 7.5 1 4.5 Z" {...stroke(p)} strokeWidth={1.1} />
      <path d="M1.5 8 L10.5 1" {...stroke(p)} strokeWidth={1.1} />
    </svg>
  ),
  /**
   * Sakelar daya: log ini dipakai menghitung dan menggambar, atau tidak.
   *
   * Sengaja bukan `eye`. Mata sudah berarti sesuatu yang lain di aplikasi ini —
   * kurva yang tidak digambar **di kolom ini**, sebuah pilihan tampilan yang
   * tidak menyentuh satu pun hitungan — dan dua arti pada satu gambar adalah
   * dua tombol yang tertukar. Yang ini memutus alirannya: sesudahnya tidak ada
   * satu pun angka di aplikasi ini yang lahir dari kurva itu.
   */
  power: (p: IconProps = {}) => (
    <svg width={p.size ?? 12} height={p.size ?? 12} viewBox="0 0 12 12" aria-hidden>
      <path d="M3.2 3.2 A4 4 0 1 0 8.8 3.2" {...stroke(p)} strokeWidth={1.2}
            strokeLinecap="round" />
      <path d="M6 1 L6 5.2" {...stroke(p)} strokeWidth={1.2} strokeLinecap="round" />
    </svg>
  ),
  pan: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M4 8h8M8 4v8" {...stroke(p)} />
      <path d="M6 6 L4 8 L6 10M10 6 L12 8 L10 10" {...stroke(p)} />
    </Svg>
  ),
  zoomBox: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <rect x="2.5" y="2.5" width="8" height="8" {...stroke(p)} />
      <path d="M10.5 10.5 L14 14" {...stroke(p)} />
    </Svg>
  ),
  ruler: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M2 14 L14 2" {...stroke(p)} />
      <path d="M2 14h4M14 2v4" {...stroke(p)} />
    </Svg>
  ),
  section: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M2 12 L6 6 L10 9 L14 3" {...stroke(p)} strokeDasharray="3 2" />
      <circle cx="6" cy="6" r="1.3" fill={p.color ?? "currentColor"} />
      <circle cx="10" cy="9" r="1.3" fill={p.color ?? "currentColor"} />
    </Svg>
  ),
  grid: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <rect x="2.5" y="2.5" width="11" height="11" {...stroke(p)} />
      <path d="M2.5 8h11M8 2.5v11" {...stroke(p)} />
    </Svg>
  ),
  datum: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M2 8h12M2 4h12M2 12h12" {...stroke(p)} />
    </Svg>
  ),
  markerAdd: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M2 8h12" {...stroke(p)} />
      <path d="M11 5 L14 8 L11 11" {...stroke(p)} />
    </Svg>
  ),
  sort: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M4 3v10M4 13l-2-2M4 13l2-2" {...stroke(p)} />
      <path d="M9 3h5M9 7h5M9 11h5" {...stroke(p)} />
    </Svg>
  ),
  trash: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M3 4h10M6 4V2.5h4V4" {...stroke(p)} />
      <path d="M4 4l.8 9.5h6.4L12 4" {...stroke(p)} />
    </Svg>
  ),
  check: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M3 8.5 L6.5 12 L13 4" {...stroke(p)} strokeWidth={p.strokeWidth ?? 1.6} />
    </Svg>
  ),
  user: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <circle cx="8" cy="5.5" r="2.8" {...stroke(p)} />
      <path d="M2.8 14c0-2.9 2.3-4.6 5.2-4.6s5.2 1.7 5.2 4.6" {...stroke(p)} />
    </Svg>
  ),
  logout: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M6.5 2.5H3v11h3.5" {...stroke(p)} />
      <path d="M9 5.5 L12.5 8 L9 10.5M12.5 8H6" {...stroke(p)} />
    </Svg>
  ),
  sun: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <circle cx="8" cy="8" r="3" {...stroke(p)} />
      <path d="M8 1.5v1.8M8 12.7v1.8M1.5 8h1.8M12.7 8h1.8M3.4 3.4l1.3 1.3M11.3 11.3l1.3 1.3M12.6 3.4l-1.3 1.3M4.7 11.3l-1.3 1.3" {...stroke(p)} />
    </Svg>
  ),
  moon: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M13 9.6A5.6 5.6 0 0 1 6.4 3 5.6 5.6 0 1 0 13 9.6z" {...stroke(p)} />
    </Svg>
  ),
  back: (p: IconProps = {}) => (
    <Svg size={p.size}>
      <path d="M9.5 3.5 L5 8 L9.5 12.5" {...stroke(p)} />
    </Svg>
  ),
};

/**
 * Nama aplikasi sebagaimana logonya menuliskannya: "Evo" merah, "LoG" hijau.
 *
 * Satu komponen dan bukan tiga potong markup yang mirip, sebab pembelahan
 * warnanya adalah bagian dari mereknya — dan yang disalin ke tiga layar akan
 * terbelah di tempat yang berbeda pada layar keempat.
 */
export function Wordmark({ size = 12, weight = 600 }: { size?: number; weight?: number }) {
  return (
    <span style={{ font: `${weight} ${size}px var(--font-sans)`, letterSpacing: ".02em",
                   whiteSpace: "nowrap" }}>
      <span style={{ color: "var(--brand-red)" }}>Evo</span>
      <span style={{ color: "var(--brand-green)" }}>LoG</span>
    </span>
  );
}

/** Ambang tempat lambang beralih ke varian optisnya, dalam piksel. */
export const SMALL_LOGO = 20;

/**
 * Lambang aplikasi: bingkai membulat yang ditembus kurva log.
 *
 * Digambar dari berkas di `public/` dan bukan disalin ke dalam berkas ini,
 * sebab favicon dan lambang di bilah atas adalah gambar yang sama — dan dua
 * salinan geometri yang sama adalah dua gambar yang cepat atau lambat akan
 * berselisih, biasanya ketika salah satunya diperbaiki.
 *
 * Berkasnya ada dua, dan itu bukan pelanggaran janji di atas melainkan
 * penerapannya: `logo.svg` untuk ukuran besar dan `logo-16.svg` untuk yang
 * kecil, dipilih di sini menurut `size`. Yang harus tetap satu adalah
 * pasangannya — apa yang dipakai favicon harus sama dengan yang dipakai
 * lambang di bilah atas, dan itulah yang dijaga oleh tesnya.
 *
 * Warnanya tidak mengikuti tema dengan sengaja: warna merek berlaku sama di
 * tema gelap maupun terang, dan lambang yang berganti warna menurut tema
 * bukan lagi lambang yang sama.
 */
export function Logo({ size = 16 }: { size?: number }) {
  // Di bawah ambang ini yang dipakai varian optisnya. Bukan pilihan gaya:
  // lambang penuh pada 16 piksel menutup dirinya sendiri — bingkai dan kurva
  // saling bertemu sampai keduanya menjadi satu bercak — dan yang menolong di
  // sana bukan menyusutkan gambar yang sama melainkan menggambar ulang dengan
  // goresan lebih tebal dan detail yang lebih sedikit.
  const src = size <= SMALL_LOGO ? "/logo-16.svg" : "/logo.svg";
  return (
    <img
      src={src}
      width={size}
      height={size}
      alt="EvoLoG"
      style={{ display: "block", flex: "none" }}
    />
  );
}
